/**
 * Pool de eventos do painel no navegador (decisão 172; análise, seção 4.7).
 *
 * Cada evento é gravado aqui antes de qualquer envio e só sai quando o servidor confirma o lote.
 * Fica num banco próprio (`lm_activity`), separado do `eminer_local` das listas: o registro de
 * uso não muda a versão do banco das listas nem disputa a atualização dele entre abas.
 * Sem IndexedDB (janela anônima de alguns navegadores), o pool vive só em memória.
 */
import type { ActivityPriority } from './catalog';

export const ACTIVITY_DB_NAME = 'lm_activity';
const DB_VERSION = 1;
const STORE = 'outbox';
const INDEX_USER_TIME = 'byUserTime';

export interface ActivityEnvelope {
  eventId: string;
  name: string;
  v: number;
  at: string;
  surface: 'panel' | 'extension';
  sessionId: string;
  seq: number;
  [key: string]: unknown;
}

export interface OutboxRecord {
  eventId: string;
  /** Quem estava logado quando o evento aconteceu: o evento só sobe com essa identidade. */
  userId: string;
  priority: ActivityPriority;
  /** Ordem de envio (ms) e desempate pela sequência da aba. */
  createdAt: number;
  envelope: ActivityEnvelope;
}

export interface Outbox {
  put(records: OutboxRecord[]): Promise<void>;
  /** Os mais antigos do usuário, até `limit`. */
  take(userId: string, limit: number): Promise<OutboxRecord[]>;
  remove(eventIds: string[]): Promise<void>;
  count(): Promise<number>;
  /** O mais antigo de todos, para o estado do pool que vai no lote. */
  oldest(): Promise<number | null>;
  /** Corta até sobrar `max`: sai primeiro a prioridade 3, e dentro dela o mais antigo. */
  trim(max: number): Promise<Record<string, number>>;
}

function byAge(a: OutboxRecord, b: OutboxRecord): number {
  return a.createdAt - b.createdAt || a.envelope.seq - b.envelope.seq;
}

function dropOrder(a: OutboxRecord, b: OutboxRecord): number {
  return b.priority - a.priority || byAge(a, b);
}

function countByName(records: OutboxRecord[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const record of records) out[record.envelope.name] = (out[record.envelope.name] ?? 0) + 1;
  return out;
}

export function createMemoryOutbox(): Outbox {
  const records = new Map<string, OutboxRecord>();
  return {
    async put(list) {
      for (const record of list) records.set(record.eventId, record);
    },
    async take(userId, limit) {
      return Array.from(records.values())
        .filter((record) => record.userId === userId)
        .sort(byAge)
        .slice(0, limit);
    },
    async remove(ids) {
      for (const id of ids) records.delete(id);
    },
    async count() {
      return records.size;
    },
    async oldest() {
      let min: number | null = null;
      for (const record of records.values()) {
        if (min === null || record.createdAt < min) min = record.createdAt;
      }
      return min;
    },
    async trim(max) {
      if (records.size <= max) return {};
      const dropped = Array.from(records.values())
        .sort(dropOrder)
        .slice(0, records.size - max);
      for (const record of dropped) records.delete(record.eventId);
      return countByName(dropped);
    },
  };
}

function toError(error: unknown, fallback: string): Error {
  return error instanceof Error ? error : new Error(fallback);
}

function requestValue<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(toError(request.error, 'Falha no pool de eventos.'));
  });
}

export function createIndexedDbOutbox(factory: IDBFactory = indexedDB): Outbox {
  let opening: Promise<IDBDatabase> | null = null;

  const open = (): Promise<IDBDatabase> => {
    if (opening) return opening;
    const attempt = new Promise<IDBDatabase>((resolve, reject) => {
      const request = factory.open(ACTIVITY_DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: 'eventId' });
          store.createIndex(INDEX_USER_TIME, ['userId', 'createdAt']);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () =>
        reject(toError(request.error, 'Não foi possível abrir o pool de eventos.'));
      request.onblocked = () =>
        reject(new Error('O pool de eventos está bloqueado por outra aba.'));
    });
    opening = attempt;
    void attempt.catch(() => {
      if (opening === attempt) opening = null;
    });
    return attempt;
  };

  const run = async <T>(
    mode: IDBTransactionMode,
    work: (store: IDBObjectStore) => Promise<T> | T,
  ): Promise<T> => {
    const db = await open();
    const tx = db.transaction(STORE, mode);
    const done = new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(toError(tx.error, 'Falha no pool de eventos.'));
      tx.onabort = () => reject(toError(tx.error, 'O pool de eventos cancelou a gravação.'));
    });
    const result = await work(tx.objectStore(STORE));
    await done;
    return result;
  };

  const all = (): Promise<OutboxRecord[]> =>
    run('readonly', (store) => requestValue(store.getAll() as IDBRequest<OutboxRecord[]>));

  return {
    put(records) {
      return run('readwrite', (store) => {
        for (const record of records) store.put(record);
      });
    },
    take(userId, limit) {
      return run(
        'readonly',
        (store) =>
          new Promise<OutboxRecord[]>((resolve, reject) => {
            const out: OutboxRecord[] = [];
            const range = IDBKeyRange.bound([userId, -Infinity], [userId, Infinity]);
            const cursor = store.index(INDEX_USER_TIME).openCursor(range);
            cursor.onsuccess = () => {
              const current = cursor.result;
              if (!current || out.length >= limit) {
                resolve(out.sort(byAge));
                return;
              }
              out.push(current.value as OutboxRecord);
              current.continue();
            };
            cursor.onerror = () => reject(toError(cursor.error, 'Falha ao ler o pool de eventos.'));
          }),
      );
    },
    remove(ids) {
      return run('readwrite', (store) => {
        for (const id of ids) store.delete(id);
      });
    },
    count() {
      return run('readonly', (store) => requestValue(store.count()));
    },
    async oldest() {
      const records = await all();
      return records.reduce<number | null>(
        (min, record) => (min === null || record.createdAt < min ? record.createdAt : min),
        null,
      );
    },
    async trim(max) {
      const records = await all();
      if (records.length <= max) return {};
      const dropped = records.sort(dropOrder).slice(0, records.length - max);
      await run('readwrite', (store) => {
        for (const record of dropped) store.delete(record.eventId);
      });
      return countByName(dropped);
    },
  };
}

/** IndexedDB quando existe; memória quando o navegador não deixa abrir. */
export function createDefaultOutbox(): Outbox {
  if (typeof indexedDB === 'undefined') return createMemoryOutbox();
  const persistent = createIndexedDbOutbox();
  const memory = createMemoryOutbox();
  let fallback = false;
  const guard =
    <A extends unknown[], R>(fn: (o: Outbox) => (...args: A) => Promise<R>) =>
    async (...args: A): Promise<R> => {
      if (!fallback) {
        try {
          return await fn(persistent)(...args);
        } catch {
          fallback = true;
        }
      }
      return fn(memory)(...args);
    };
  return {
    put: guard((o) => o.put),
    take: guard((o) => o.take),
    remove: guard((o) => o.remove),
    count: guard((o) => o.count),
    oldest: guard((o) => o.oldest),
    trim: guard((o) => o.trim),
  };
}
